/**
 * Explain — the evidence layer.
 *
 * A till gets asked "why is it this much?" — why 18%, why ₹2 of round-off, why
 * this customer owes what they owe. The number alone is an assertion; this puts
 * the working behind it one gesture away.
 *
 * Rules it follows, because a tooltip is easy to misuse:
 *
 *  1. It carries EVIDENCE, never anything the sale depends on. Everything
 *     needed to ring up a bill stays visible on the surface. If a thing has to
 *     be read to complete the sale, it does not belong in here.
 *  2. It opens on hover, on tap and from the keyboard. Built on Popover rather
 *     than a hover-only tooltip on purpose: a tooltip is unreachable on a touch
 *     till, and plenty of these run on touch screens.
 *  3. The trigger is marked. A dotted underline says "there is more here";
 *     without it the information may as well not exist, because nobody hovers
 *     a number at random.
 */
import { useRef, useState, type ReactNode } from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { cn } from '@/utils/cn';

export function Explain({
  children,
  title,
  content,
  side = 'top',
  className,
  underline = true,
}: {
  /** The thing being explained — a number, a label, a status. */
  children: ReactNode;
  /** Short heading for the panel. */
  title?: string;
  /** The working. Keep it to a few rows; this is a glance, not a report. */
  content: ReactNode;
  side?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
  /** Turn off only where the trigger already reads as interactive. */
  underline?: boolean;
}) {
  const [open, setOpen] = useState(false);
  // Hover intent. Opening instantly makes a dense row flicker as the pointer
  // crosses it; closing instantly makes the panel impossible to move into.
  const enterTimer = useRef<number | undefined>(undefined);
  const leaveTimer = useRef<number | undefined>(undefined);

  const clear = () => {
    window.clearTimeout(enterTimer.current);
    window.clearTimeout(leaveTimer.current);
  };
  const hoverOpen = () => {
    clear();
    enterTimer.current = window.setTimeout(() => setOpen(true), 180);
  };
  const hoverClose = () => {
    clear();
    leaveTimer.current = window.setTimeout(() => setOpen(false), 120);
  };

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          // A real button, so Tab reaches it and Enter opens it. Radix handles
          // Escape and focus return.
          onMouseEnter={hoverOpen}
          onMouseLeave={hoverClose}
          onFocus={() => setOpen(true)}
          onBlur={hoverClose}
          className={cn(
            'inline-flex items-center rounded-sm text-left transition-colors',
            underline && 'underline decoration-dotted decoration-fg-tertiary underline-offset-[3px]',
            className,
          )}
        >
          {children}
        </button>
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          side={side}
          align="end"
          sideOffset={6}
          collisionPadding={12}
          onOpenAutoFocus={(e) => {
            // Hovering should not steal focus from the scan box — losing focus
            // mid-sale is how a scan ends up typed into nothing.
            if (!open) return;
            e.preventDefault();
          }}
          onMouseEnter={clear}
          onMouseLeave={hoverClose}
          className={cn(
            'z-50 w-[17rem] rounded-lg border border-stroke-hairline/15 bg-bg-surface p-3 shadow-e3',
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
        >
          {title && (
            <div className="mb-2 text-label uppercase text-fg-tertiary">{title}</div>
          )}
          <div className="space-y-1.5 text-body text-fg-secondary">{content}</div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

/** One label/value line inside an Explain panel. */
export function ExplainRow({
  label,
  value,
  strong,
}: {
  label: ReactNode;
  value: ReactNode;
  strong?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-fg-secondary">{label}</span>
      <span className={cn('shrink-0', strong ? 'font-semibold text-fg-primary' : 'text-fg-primary')}>
        {value}
      </span>
    </div>
  );
}
